import React, { useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import ReusableDatePicker from "../../../components/ReusableDatePicker/ReusableDatePicker";
import CustomDayPickerInput from "../../../components/Cart/components/CustomDayPickerInput";
import { updateOrderTime, showAddressModal } from "../../../redux/actions";
import { Form, Field } from "react-final-form";
import _ from "lodash";
import {
  PhoneField,
  Button,
  getIcon,
  Dropdown,
} from "@dishopsaas/design-system";
import MapWithLatLng from "../../../components/Map/MapWithLatLng";
import { CollapsibleCardBox } from "../../../components/Collapse/Collapse";
import {
  TRANSPARENT,
  ORDER_TYPE_DELIVERY,
  NO_SLOT_AVAILABLE_MESSAGE,
  ICONS,
} from "../../../constants";
import { getAddress } from "../../../components/InfoBar/InfoBar.services";
import { getOrderTypeLabel } from "../../../utils/order";
import { OrderTypesSectionProps } from "../../../1_types/paymentSummaryTypes";
import { openMessageModal } from "../../../utils/common";
import { isQrCode } from "../../../utils";

const OrderTypesSection: React.FC<OrderTypesSectionProps> = ({
  dispatch,
  address,
  phone,
  setPhone,
  phoneObj,
  setPhoneObj,
  googleApiKey,
  secondaryColor,
  setIsPhoneIncorrect,
  isPhoneIncorrect,
  setDateState,
  day,
  orderTimes,
  selectTime,
  isValidDate,
  orderTime,
  hourSelectValue,
  configHours,
  orderType,
  shopName,
  isMobile,
  theme,
  closed,
}) => {
  const orderTypeLabel = getOrderTypeLabel(orderType);
  const prevOpenHours = useRef(orderTimes);
  const showScheduledTime = day && !configHours?.hideScheduledTime;

  useEffect(() => {
    if (!_.isEqual(prevOpenHours.current, orderTimes)) {
      setDateState({
        hourSelectValue: 0,
        selectTime: false,
        isValidDate: true,
      });
      prevOpenHours.current = orderTimes;
    }
  }, [orderTimes, setDateState]);

  const disabledTime =
    !orderTimes?.length || (orderTimes[0]?.disabled && !selectTime) || !!closed;

  const noHoverClickSx = {
    p: 0,
    "&, &:hover, &:active, &:focus": {
      backgroundColor: "transparent",
      boxShadow: "none",
      outline: "none",
    },
  };

  const renderPhoneInput = ({ input }: any) => (
    <PhoneField
      label="Téléphone"
      value={input.value}
      onChange={(value) => {
        setPhoneObj({ ...phoneObj, number: value });
        setPhone(value);
        input.onChange(value);
      }}
      isPhoneIncorrect={setIsPhoneIncorrect}
      getPhoneObj={(obj) => !_.isEqual(obj, phoneObj) && setPhoneObj(obj)}
      error={isPhoneIncorrect}
    />
  );

  const hasLocation =
    address?.location?.latitude && address?.location?.longitude;
  const isDelivery = orderType === ORDER_TYPE_DELIVERY;
  const textSx = {
    textTransform: "none",
    fontWeight: 400,
    fontSize: isMobile ? 12 : 14,
  };

  const iconName = isDelivery ? "position" : "Store";
  const label = isDelivery ? "Adresse:" : "Magasin:";
  const value = isDelivery ? getAddress() : shopName;

  return (
    <CollapsibleCardBox
      title={
        <>
          Commande {orderTypeLabel}
          <Box display="flex" alignItems="center" flexWrap="wrap" pt={1}>
            <Box display="flex" alignItems="center" gap={2} pr={4}>
              {getIcon(iconName, { size: 16 })}
              <Typography variant="body1" sx={textSx}>
                {label} {value}
              </Typography>
            </Box>

            {showScheduledTime && orderTime?.value && (
              <Box display="flex" alignItems="center" gap={2}>
                {getIcon("HandPlatter", { size: 16 })}
                <Typography variant="body1" sx={textSx}>
                  Créneau horaire entre: {orderTime.value}
                </Typography>
              </Box>
            )}
          </Box>
        </>
      }
    >
      <Box px={3} pt={3}>
        <Box
          display="flex"
          gap={3}
          pb={3}
          sx={{ flexDirection: { xs: "column", sm: "row" } }}
        >
          <Box flex={1}>
            {showScheduledTime && (
              <>
                <Typography mb={1}>Jour :</Typography>
                <ReusableDatePicker
                  selected={closed ? null : day?.toDate()}
                  setDateState={setDateState}
                  closed={closed}
                  maxDays={5}
                  customInput={
                    <CustomDayPickerInput
                      isValidDate={isValidDate}
                      orderTime={orderTime?.value}
                      closed={closed}
                    />
                  }
                />
                {!isValidDate && !orderTime && (
                  <Typography color="error" variant="caption">
                    Veuillez choisir une date
                  </Typography>
                )}
              </>
            )}

            {isDelivery && !isQrCode() && (
              <Box pt={showScheduledTime ? 2 : 0}>
                <Form
                  onSubmit={() => {}}
                  initialValues={{ numero: phone || "" }}
                  render={() => (
                    <Field name="numero" render={renderPhoneInput} />
                  )}
                />
              </Box>
            )}
          </Box>

          {showScheduledTime && (
            <Box flex={1}>
              <Typography mb={1}>Créneau horaire :</Typography>
              <Dropdown
                placeholder="Sélectionner..."
                placeholderIcon={ICONS.Clock}
                label=""
                disabled={!!closed || !orderTimes?.length}
                value={
                  disabledTime || orderTimes[hourSelectValue]?.disabled
                    ? ""
                    : hourSelectValue
                }
                options={orderTimes.map((t, i) => ({
                  label: t.value,
                  value: i,
                  disabled: t.disabled,
                  leftIcon: ICONS.Clock,
                }))}
                onOpen={() => {
                  if (!orderTimes?.length)
                    openMessageModal(NO_SLOT_AVAILABLE_MESSAGE, {
                      text: "Aucun créneau disponible",
                      title: "Aucun créneau disponible",
                      subTitle: NO_SLOT_AVAILABLE_MESSAGE,
                    });
                }}
                onChange={(selectedValue) => {
                  const selectedTime = orderTimes[selectedValue];
                  if (selectedTime && !selectedTime.disabled) {
                    dispatch(updateOrderTime(selectedTime));
                    setDateState({
                      hourSelectValue: selectedValue,
                      selectTime: true,
                      isValidDate: true,
                    });
                  }
                }}
              />
              {!isValidDate && orderTime?.disabled && (
                <Typography color="error" variant="caption">
                  Veuillez choisir une heure
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{ px: 3, pb: 3 }}
        display="flex"
        justifyContent="space-between"
        gap={3}
        flexWrap="wrap"
        alignItems="stretch"
      >
        <Box flex={1}>
          {hasLocation && (
            <MapWithLatLng
              isDelivery={isDelivery}
              lat={address.location.latitude}
              lng={address.location.longitude}
              apiKey={googleApiKey}
              secondaryColor={secondaryColor}
              theme={theme}
              isInteractive={false}
            />
          )}

          <Typography variant="caption" sx={{ pt: 2 }}>
            {isDelivery ? "Adresse de livraison: " : "Magasin: "}
          </Typography>

          <Typography variant="body1">
            {isDelivery ? getAddress() : shopName}
          </Typography>

          <Button
            disableRipple
            sx={noHoverClickSx}
            iconOptions={{ color: secondaryColor, size: isMobile ? 12 : 14 }}
            labelSx={{ color: secondaryColor, fontSize: isMobile ? 12 : 14 }}
            variant="text"
            label={isDelivery ? "Modifier l'adresse" : "Modifier le magasin"}
            colorType={TRANSPARENT}
            borderRadius={8}
            rightIcon={ICONS.ChevronRight}
            leftIcon={ICONS.Pencil}
            onClick={() => dispatch(showAddressModal(true))}
          />
        </Box>
      </Box>
    </CollapsibleCardBox>
  );
};

export default OrderTypesSection;
